import { Controller } from '@hotwired/stimulus'

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
type FieldValue = string | boolean | string[]
type TurboSubmitEndEvent = Event & { detail?: { success?: boolean } }

interface Turbo {
  renderStreamMessage: (html: string) => void
}

export default class CancelChangesController extends Controller<HTMLFormElement> {
  static targets = ['button']
  static outlets = ['modal']
  private static readonly submittedBaselines = new Map<string, FieldValue[]>()

  declare readonly buttonTarget: HTMLButtonElement
  declare readonly modalOutlet: { open: (event?: Event) => void }

  private baseline: FieldValue[] = []
  private readonly refresh = (): void => { this.updateButton() }
  private readonly rememberBaseline = (): void => {
    CancelChangesController.submittedBaselines.set(this.formKey(), this.baseline)
  }

  private readonly forgetSuccessfulBaseline = (event: Event): void => {
    if ((event as TurboSubmitEndEvent).detail?.success === true) {
      CancelChangesController.submittedBaselines.delete(this.formKey())
    }
  }

  private baselineCaptured = false

  connect (): void {
    this.buttonTarget.disabled = true
    this.element.addEventListener('input', this.refresh, true)
    this.element.addEventListener('change', this.refresh, true)
    this.element.addEventListener('turbo:submit-start', this.rememberBaseline)
    this.element.addEventListener('turbo:submit-end', this.forgetSuccessfulBaseline)

    setTimeout(() => {
      this.captureBaseline()
      this.updateButton()
    }, 25)
  }

  disconnect (): void {
    this.element.removeEventListener('input', this.refresh, true)
    this.element.removeEventListener('change', this.refresh, true)
    this.element.removeEventListener('turbo:submit-start', this.rememberBaseline)
    this.element.removeEventListener('turbo:submit-end', this.forgetSuccessfulBaseline)
  }

  saved (): void {
    this.captureBaseline()
    this.updateButton()
  }

  request (event: Event): void {
    event.preventDefault()
    if (this.isPristine()) return

    this.modalOutlet.open(event)
  }

  discard (): void {
    CancelChangesController.submittedBaselines.delete(this.formKey())
    void this.replaceCurrentForm()
  }

  private updateButton (): void {
    if (!this.baselineCaptured) {
      this.buttonTarget.disabled = true
      return
    }

    this.buttonTarget.disabled = this.isPristine()
  }

  private isPristine (): boolean {
    return this.currentValues().every((value, index) => this.valuesEqual(value, this.baseline[index]))
  }

  private captureBaseline (): void {
    this.baseline = CancelChangesController.submittedBaselines.get(this.formKey()) ?? this.currentValues()
    CancelChangesController.submittedBaselines.delete(this.formKey())
    this.baselineCaptured = true
  }

  private currentValue (field: Field): FieldValue {
    if (field instanceof HTMLInputElement && ['checkbox', 'radio'].includes(field.type)) {
      return field.checked
    }

    if (field instanceof HTMLSelectElement && field.multiple) {
      return Array.from(field.selectedOptions).map((option) => option.value)
    }

    return field.value
  }

  private valuesEqual (current: FieldValue, baseline: FieldValue | undefined): boolean {
    if (Array.isArray(current) && Array.isArray(baseline)) {
      return current.length === baseline.length && current.every((value, index) => value === baseline[index])
    }

    return current === baseline
  }

  private formKey (): string {
    return this.element.id !== '' ? this.element.id : this.element.action
  }

  private async replaceCurrentForm (): Promise<void> {
    const response = await fetch(this.formReloadUrl(), {
      headers: {
        Accept: 'text/vnd.turbo-stream.html'
      }
    })
    if (!response.ok) return

    this.turbo()?.renderStreamMessage(await response.text())
  }

  private formReloadUrl (): string {
    const url = new URL(this.element.action)
    url.pathname = `${url.pathname.replace(/\/$/, '')}/edit`
    url.searchParams.set('form_name', this.formName())

    return url.toString()
  }

  private formName (): string {
    const input = this.element.querySelector<HTMLInputElement>('input[name="form_name"]')
    return input?.value ?? ''
  }

  private currentValues (): FieldValue[] {
    return this.fields().map((field) => this.currentValue(field))
  }

  private turbo (): Turbo | undefined {
    return (window as unknown as { Turbo?: Turbo }).Turbo
  }

  private fields (): Field[] {
    return Array.from(this.element.elements)
      .filter((element): element is Field => (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ))
      .filter((field) => !field.disabled)
      .filter((field) => field.dataset.cancelChangesIgnore !== 'true')
      .filter((field) => !['button', 'file', 'reset', 'submit'].includes(field.type))
  }
}
